import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import EventCard from "@/components/EventCard";
import { useLang } from "@/context/LanguageContext";
import { haversineKm } from "@/lib/geo";
import { MapPin, Navigation, Loader2 } from "lucide-react";

const RADII = [25, 50, 75, 100];

export default function NearYou({ events = [] }) {
  const { t } = useLang();
  const [coords, setCoords] = useState(null);
  const [state, setState] = useState("idle"); // idle | locating | ok | denied
  const [radius, setRadius] = useState(50);

  const locate = () => {
    if (!navigator.geolocation) { setState("denied"); return; }
    setState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setState("ok"); },
      () => setState("denied"),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  };

  // Auto-request on mount.
  useEffect(() => { locate(); /* eslint-disable-next-line */ }, []);

  const nearby = useMemo(() => {
    if (!coords) return [];
    const now = new Date();
    return events
      .filter((e) => e.lat && e.lng && e.start_date && new Date(e.end_date || e.start_date) >= now)
      .map((e) => ({ ...e, _distanceKm: haversineKm(coords.lat, coords.lng, e.lat, e.lng) }))
      .filter((e) => e._distanceKm <= radius)
      .sort((a, b) => a._distanceKm - b._distanceKm)
      .slice(0, 12);
  }, [coords, events, radius]);

  return (
    <section data-testid="home-near-you" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 border-b border-amber-900/30 pb-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2">
            <Navigation className="w-3.5 h-3.5" /> {t("nearyou_tag")}
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100">{t("nearyou_title")}</h2>
        </div>
        {state === "ok" && (
          <div className="flex items-center gap-1.5" data-testid="nearyou-radius-group">
            {RADII.map((r) => (
              <button
                key={r}
                data-testid={`nearyou-radius-${r}`}
                onClick={() => setRadius(r)}
                className={`px-3.5 py-1.5 rounded-full font-mono text-xs tracking-wide border transition-all ${
                  radius === r
                    ? "bg-amber-600 text-stone-950 border-amber-400 font-bold"
                    : "bg-[#181A20] text-stone-300 border-[#272A34] hover:border-amber-500/50"
                }`}
              >
                {r} km
              </button>
            ))}
          </div>
        )}
      </div>

      {state === "locating" && (
        <div className="flex items-center gap-3 text-stone-400 py-8" data-testid="nearyou-locating">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" /> {t("nearyou_locating")}
        </div>
      )}

      {state === "denied" && (
        <div className="rounded-xl border border-[#272A34] bg-[#121418] p-8 text-center" data-testid="nearyou-denied">
          <MapPin className="w-8 h-8 text-amber-600/70 mx-auto mb-3" />
          <p className="text-stone-400 max-w-md mx-auto mb-5">{t("nearyou_denied")}</p>
          <button
            data-testid="nearyou-retry-btn"
            onClick={locate}
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-2.5 rounded-md transition-all"
          >
            <Navigation className="w-4 h-4" /> {t("nearyou_locate")}
          </button>
        </div>
      )}

      {state === "ok" && nearby.length === 0 && (
        <p className="text-stone-500 py-8" data-testid="nearyou-none">{t("nearyou_none")}</p>
      )}

      {state === "ok" && nearby.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {nearby.map((e, i) => <EventCard key={e.slug} event={e} index={i} distanceKm={e._distanceKm} />)}
        </motion.div>
      )}
    </section>
  );
}
